import React, { useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatDate } from '../../utils/dateFormatter';
import { TEXT, VERDICTS, VERDICT_HELP, VERDICT_HELP_BOOKINGCOM, TREND, COLLECTION, verdictReason, thresholdValueText } from '../../config/listingPerformanceLabels';

/**
 * «أداء الوحدات» — the small pieces every table on the page shares.
 *
 * Digits: Western, deliberately (founder rule, 20 Sep — `tasks/AttentionTab.jsx`): these are figures ops
 * scan and compare down a column, and the sheet they replace shows them the same way. Dates do NOT pass
 * through here — they go to utils/dateFormatter.js.
 *
 * Blank is never zero: every formatter returns null for a missing figure and <Figure> shows «—» with
 * «لم يُقرأ» as its hint — never a 0.
 */

const COUNT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const DEC1 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const DEC2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const AVG = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const DEC1_MAX = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Help text that shows as soon as the pointer is on it, and on keyboard focus. The browser's own `title`
 * tooltip waits for the pointer to rest and often for a further move, and never shows on focus or touch
 * (founder, 16 Oct). Same pattern as the scorecard's info hint (opsDashboard/ScoreCard.jsx).
 * `tap` also opens it on a press — only where a press does nothing else (a header, the settings line),
 * never on a cell inside a row, where a press opens the unit.
 * The short show delay stops a row of hints flashing as the pointer crosses the table.
 */
export const Hint = ({ text, tap = false, children }) => {
  if (!text) return children;
  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 150, hide: 0 }}
      trigger={tap ? ['hover', 'focus', 'click'] : ['hover', 'focus']}
      rootClose={tap}
      overlay={<Tooltip dir="rtl" className="lp-tip">{text}</Tooltip>}
    >
      {children}
    </OverlayTrigger>
  );
};

/** «—» for a figure nobody read, with «لم يُقرأ» on hover. */
const Missing = () => <Hint text={TEXT.notRead}><span className="lp-missing">—</span></Hint>;

/** A figure as text, by kind. null when there is nothing to show. */
export const formatFigure = (value, kind) => {
  if (kind === 'text') return value ? String(value) : null;
  if (!isNum(value)) return null;
  switch (kind) {
    case 'pct1': return `${DEC1.format(value)}%`;
    case 'pct2': return `${DEC2.format(value)}%`;
    case 'rating': return AVG.format(value);
    case 'money': return COUNT.format(value);
    case 'avg': return AVG.format(value);
    case 'avg1': return DEC1_MAX.format(value);
    case 'round': return COUNT.format(Math.round(value));
    default: return Number.isInteger(value) ? COUNT.format(value) : AVG.format(value);
  }
};

/**
 * An average of whole things reads as a whole number once it is 10 or more (822, not 822.25); below 10
 * the decimal carries meaning (1.5 bookings a week). Display only — the server's figure is unchanged.
 */
export const averageKind = (kind, value) => {
  if (kind !== 'count') return kind;
  return typeof value === 'number' && Math.abs(value) >= 10 ? 'round' : 'avg1';
};

/** The collector's reading notes, folded to three lines — they explain a week, they are not the week. */
export const ReadingNotes = ({ notes, readOn }) => {
  const [open, setOpen] = useState(false);
  if (!notes && !readOn) return null;
  return (
    <div className="lp-notes">
      <span className="lp-notes__label">{TEXT.notes}</span>
      {readOn && <span className="lp-muted"> — {TEXT.readOn} {formatDate(readOn)}</span>}
      {notes && <p className={open ? '' : 'lp-clamp'}>{notes}</p>}
      {notes && notes.length > 180 && (
        <Button variant="link" size="sm" className="lp-link lp-notes__toggle" onClick={() => setOpen(o => !o)}>
          {open ? TEXT.less : TEXT.more}
        </Button>
      )}
    </div>
  );
};

/** «هدف الإشغال 60% ← 50%» — one threshold change, in words. */
export const ThresholdChanges = ({ changes = [] }) => changes.map((c, i) => (
  <span key={c.field} className="lp-threshold-change">
    {i > 0 && '، '}
    {TEXT.thresholdShort[c.field] || c.field} <bdi className="lp-num">{thresholdValueText(c.field, c.from)}</bdi> ← <bdi className="lp-num">{thresholdValueText(c.field, c.to)}</bdi>
  </span>
));

/** A figure in a cell: «—» when missing, isolated so a % sign never jumps sides in RTL. */
export const Figure = ({ value, kind = 'count', quietZero = false }) => {
  const text = formatFigure(value, kind);
  if (text === null) return <Missing />;
  if (kind === 'text') return <span>{text}</span>;
  // In a column of mostly zeros, a zero is greyed so the few real figures stand out. A zero is still a zero.
  return <bdi className={`lp-num${quietZero && value === 0 ? ' lp-zero' : ''}`}>{text}</bdi>;
};

/** ↑ / ↓ / = against the prior week. Colour says direction only — whether it is good is the reader's call. */
export const TrendArrow = ({ trend }) => {
  if (!trend) return <span className="lp-trend lp-trend--none" aria-hidden="true" />;
  const t = TREND[trend];
  // No hover text: the glyph already says it; the label is for screen readers.
  return <span className={`lp-trend lp-trend--${trend}`} aria-label={t.label}>{t.glyph}</span>;
};

/** The rule that fired, in words, with Western digits — for a chip's hover and the detail's «لماذا». */
export const reasonText = (code, inputs, platform = 'airbnb') => (platform === 'bookingcom'
  ? VERDICT_HELP_BOOKINGCOM[code] || ''
  : verdictReason(code, inputs, (v) => (isNum(v) ? formatFigure(v, Number.isInteger(v) ? 'count' : 'avg') : '—')));

export const VerdictChip = ({ code, inputs, platform = 'airbnb' }) => {
  if (!code) return null;
  const v = VERDICTS[code] || { label: code, tone: 'neutral' };
  const help = reasonText(code, inputs, platform) || (platform === 'bookingcom' ? VERDICT_HELP_BOOKINGCOM : VERDICT_HELP)[code];
  return <Hint text={help}><span className={`lp-chip lp-chip--${v.tone}`}>{v.label}</span></Hint>;
};

/**
 * «تنبيه شارة» — the unit lost Guest Favourite this week (server: rules.guestFavouriteLost). A tag beside the
 * verdict, never a verdict of its own: a unit can be «محجوز معظم الأسبوع» AND lose its badge (D6).
 */
export const BadgeLostTag = ({ hint = true }) => {
  const tag = <span className="lp-badge-alert"><i className="bi bi-award" aria-hidden="true" /> {TEXT.badgeLost}</span>;
  return hint ? <Hint text={TEXT.badgeLostHint}>{tag}</Hint> : tag;
};

/** The verdict, with the badge tag beside it when the unit lost Guest Favourite. */
export const VerdictWithTag = ({ code, inputs, badgeLost }) => (
  <span className="lp-verdict">
    <VerdictChip code={code} inputs={inputs} />
    {badgeLost && <BadgeLostTag />}
  </span>
);

/**
 * THE row's lead figure: occupancy %, with a short bar and a tick at the target, in its band colour.
 * In the owner view there is no band and no target (they encode the thresholds) — the bar is neutral.
 */
export const OccupancyCell = ({ cell }) => {
  const value = cell?.value;
  // The target the week was judged with (sent with the row); none in the owner view.
  const tick = cell?.target;
  if (!isNum(value)) return <Missing />;
  const band = cell.band || 'neutral';
  return (
    <span className={`lp-occ lp-occ--${band}`}>
      <bdi className="lp-num lp-occ__value">{formatFigure(value, 'pct1')}</bdi>
      <span className="lp-occ__track" aria-hidden="true">
        <span className="lp-occ__fill" style={{ inlineSize: `${Math.min(100, Math.max(0, value))}%` }} />
        {isNum(tick) && <span className="lp-occ__target" style={{ insetInlineStart: `${Math.min(100, tick)}%` }} />}
      </span>
      <TrendArrow trend={cell.trend} />
    </span>
  );
};

/** A figure followed by its arrow — the board's standard cell. */
export const FigureWithTrend = ({ cell, kind = 'count' }) => (
  <span className="lp-cell">
    <Figure value={cell?.value ?? null} kind={kind} />
    <TrendArrow trend={cell?.trend} />
  </span>
);

/**
 * Did this week's numbers arrive? Ops care about the exception, so: one quiet ✓ when every platform that
 * applies has arrived; otherwise the platform and what is wrong with it, in words.
 */
export const CollectionStatus = ({ collection }) => {
  if (!collection) return null;
  const applies = [['Airbnb', collection.airbnb], ['Booking.com', collection.bookingcom]].filter(([, s]) => s && s !== 'not_applicable');
  if (!applies.length) {
    return <Hint text={COLLECTION.not_applicable.label}><span className="lp-collect lp-collect--not_applicable">—</span></Hint>;
  }
  const problems = applies.filter(([, s]) => s !== 'received');
  if (!problems.length) {
    return (
      <Hint text={`${TEXT.allReceived}: ${applies.map(([p]) => p).join('، ')}`}>
        <span className="lp-collect lp-collect--received">
          <i className="bi bi-check-circle" aria-hidden="true" /><span className="visually-hidden">{TEXT.allReceived}</span>
        </span>
      </Hint>
    );
  }
  return (
    <span className="lp-collect__problems">
      {problems.map(([p, s]) => (
        <span key={p} className={`lp-collect lp-collect--${s}`}>
          <i className={`bi ${COLLECTION[s].icon}`} aria-hidden="true" /> {COLLECTION[s].label} <bdi>{p}</bdi>
        </span>
      ))}
    </span>
  );
};
